import PageHeader from '@/components/shared/PageHeader';
import { WorkOrderStatusBadge } from '@/components/shared/StatusBadge';
import {
  LayoutDashboard, ClipboardList, Package, Cpu, AlertTriangle,
  Truck, Shield, FileText, BarChart3, Settings, Calendar, Download, RefreshCw, Info,
  BoxesIcon, Users, Plus, Search, Filter, ArrowUpDown, MoreHorizontal,
  Monitor, HardDrive, ShieldCheck, Wrench, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useWorkOrders } from '@/hooks/useWorkOrders';
import { useDashboard } from '@/hooks/useDashboard';
function PlaceholderPage({ title, description, icon }: { title: string; description: string; icon: any }) {
  return (
    <>
      <PageHeader title={title} description={description} icon={icon} />
      <Card>
        <CardContent className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">This module will be built in an upcoming phase.</p>
        </CardContent>
      </Card>
    </>
  );
}

// ==================== DASHBOARD ====================

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: any;
  color?: 'blue' | 'amber' | 'green' | 'red' | 'purple';
}

function KpiCard({ label, value, icon: Icon, color = 'blue' }: KpiCardProps) {
  const colorMap = {
    blue: 'text-[hsl(var(--status-received))] bg-[hsl(var(--status-received)/0.1)]',
    amber: 'text-[hsl(var(--status-processing))] bg-[hsl(var(--status-processing)/0.1)]',
    green: 'text-[hsl(var(--status-complete))] bg-[hsl(var(--status-complete)/0.1)]',
    red: 'text-[hsl(var(--destructive))] bg-[hsl(var(--destructive)/0.1)]',
    purple: 'text-[hsl(var(--status-testing))] bg-[hsl(var(--status-testing)/0.1)]',
  };
  const [iconColor, bgColor] = colorMap[color].split(' ');

  return (
    <Card className="flex-1 min-w-[180px]">
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${bgColor}`}>
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

const throughputConfig = {
  received: { label: 'Received', color: 'hsl(var(--status-received))' },
  processed: { label: 'Processed', color: 'hsl(var(--status-complete))' },
};

const PIE_COLORS = [
  'hsl(var(--status-received))',
  'hsl(var(--status-processing))',
  'hsl(var(--status-testing))',
  'hsl(var(--status-complete))',
  'hsl(var(--muted-foreground))',
];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useDashboard();

  if (isError) {
    return (
      <>
        <PageHeader title="Dashboard" description="Operations overview" icon={LayoutDashboard} />
        <Card><CardContent className="flex h-64 items-center justify-center">
          <p className="text-destructive">Failed to load dashboard data. Please try again.</p>
        </CardContent></Card>
      </>
    );
  }

  const kpis = data?.kpis;
  const throughput = data?.throughput ?? [];
  const categories = data?.category_breakdown ?? [];
  const upcoming = data?.upcoming_work_orders ?? [];
  const recent = data?.recent_work_orders ?? [];

  return (
    <>
      <PageHeader title="Dashboard" description="Operations overview" icon={LayoutDashboard} />

      {/* KPI Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="flex-1 min-w-[180px]">
              <CardContent className="p-4"><Skeleton className="h-12 w-full" /></CardContent>
            </Card>
          ))
        ) : (
          <>
            <KpiCard label="Received Today" value={kpis?.received_today ?? 0} icon={Package} color="blue" />
            <KpiCard label="Processed Today" value={kpis?.processed_today ?? 0} icon={Cpu} color="green" />
            <KpiCard label="Pending Wipes" value={kpis?.pending_wipes ?? 0} icon={ShieldCheck} color="amber" />
            <KpiCard label="Open Exceptions" value={kpis?.open_exceptions ?? 0} icon={AlertTriangle} color="red" />
            <KpiCard label="Revenue Recovered" value={`$${(kpis?.revenue_recovered ?? 0).toLocaleString()}`} icon={BarChart3} color="green" />
            <KpiCard label="Pending Shipments" value={kpis?.pending_shipments ?? 0} icon={Truck} color="purple" />
          </>
        )}
      </div>

      {/* Upcoming Scheduled Work Orders */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" /> Upcoming Pickups & Drop-offs
            </h2>
            <Button size="sm" variant="outline" onClick={() => navigate('/work-orders/new')}>
              <Plus className="h-4 w-4 mr-1.5" /> New Work Order
            </Button>
          </div>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No upcoming work orders scheduled.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">WO #</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Scheduled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.map((wo) => (
                  <TableRow key={wo.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/work-orders/${wo.id}`)}>
                    <TableCell className="font-mono text-sm font-medium">{wo.work_order_number}</TableCell>
                    <TableCell className="text-sm">{wo.customer_name}</TableCell>
                    <TableCell className="text-sm capitalize">{wo.work_order_type}</TableCell>
                    <TableCell><WorkOrderStatusBadge status={wo.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{wo.scheduled_date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <h2 className="mb-4 text-sm font-semibold text-foreground">Processing Throughput (This Week)</h2>
            {isLoading ? <Skeleton className="h-[260px] w-full" /> : (
              <ChartContainer config={throughputConfig} className="h-[260px] w-full">
                <BarChart data={throughput}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="received" fill="var(--color-received)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="processed" fill="var(--color-processed)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h2 className="mb-4 text-sm font-semibold text-foreground">Material Recovery</h2>
            {isLoading ? <Skeleton className="h-[260px] w-full" /> : (
              <div className="flex h-[260px] items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={categories} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                      {categories.map((_, index) => (
                        <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Work Orders */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Recent Work Orders</h2>
            <Button size="sm" variant="outline" asChild>
              <a href="/work-orders">View All</a>
            </Button>
          </div>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">WO #</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Assets</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((wo) => (
                  <TableRow key={wo.id}>
                    <TableCell className="font-mono text-sm">{wo.work_order_number}</TableCell>
                    <TableCell className="text-sm">{wo.customer_name}</TableCell>
                    <TableCell><WorkOrderStatusBadge status={wo.status} /></TableCell>
                    <TableCell className="text-sm">{wo.assets_received} / {wo.assets_processed}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{wo.scheduled_date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

// ==================== WORK ORDERS ====================

export const WorkOrdersPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { data: workOrders = [], isLoading } = useWorkOrders();

  const filtered = workOrders.filter((wo) => {
    const matchesSearch = !search || wo.work_order_number.toLowerCase().includes(search.toLowerCase()) || wo.customer_name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wo.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <PageHeader title="Work Orders" description="Manage pickup and drop-off work orders" icon={ClipboardList} />

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search WO #, customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-9 text-sm">
            <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="intake_complete">Intake Complete</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" className="gap-2 ml-auto" onClick={() => navigate('/work-orders/new')}>
          <Plus className="h-4 w-4" /> New Work Order
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">WO #</TableHead>
                <TableHead className="text-xs">Customer</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Assets Recv'd</TableHead>
                <TableHead className="text-xs">Assets Proc'd</TableHead>
                <TableHead className="text-xs">Operator</TableHead>
                <TableHead className="text-xs">Scheduled</TableHead>
                <TableHead className="text-xs w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((wo) => (
                <TableRow
                  key={wo.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/work-orders/${wo.id}`)}
                >
                  <TableCell className="font-mono text-sm font-medium">{wo.work_order_number}</TableCell>
                  <TableCell className="text-sm">{wo.customer_name}</TableCell>
                  <TableCell className="text-sm capitalize">{wo.work_order_type}</TableCell>
                  <TableCell><WorkOrderStatusBadge status={wo.status} /></TableCell>
                  <TableCell className="text-sm text-center">{wo.assets_received}</TableCell>
                  <TableCell className="text-sm text-center">{wo.assets_processed}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{wo.assigned_operator || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{wo.scheduled_date}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No work orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

// ==================== PLACEHOLDER PAGES ====================

// WorkOrderDetailPage and IntakePage are now standalone files

export const AssetsPage = () => <PlaceholderPage title="Assets" description="Track all assets across sites" icon={Package} />;
export const AssetDetailPage = () => <PlaceholderPage title="Asset Details" description="Full asset record, processing, and audit log" icon={Package} />;

export const ContainersPage = () => <PlaceholderPage title="Containers / Pallets" description="Group and track assets by container" icon={BoxesIcon} />;
export const ContainerDetailPage = () => <PlaceholderPage title="Container Details" description="Container contents and location" icon={BoxesIcon} />;

export const ProcessingPage = () => <PlaceholderPage title="Processing" description="Triage, wipe, repair, and QA queues" icon={Cpu} />;
export const TriagePage = () => <PlaceholderPage title="Triage" description="Initial assessment and categorization" icon={Cpu} />;
export const WipePage = () => <PlaceholderPage title="Data Wipe" description="Sanitization queue and records" icon={ShieldCheck} />;
export const RepairPage = () => <PlaceholderPage title="Repair" description="Repair and refurbishment queue" icon={Wrench} />;
export const QAPage = () => <PlaceholderPage title="Quality Assurance" description="QA testing and pass/fail" icon={CheckCircle2} />;

export const ExceptionsPage = () => <PlaceholderPage title="Exceptions" description="Serial mismatches, wipe failures, and resolution" icon={AlertTriangle} />;
export const InventoryPage = () => <PlaceholderPage title="Inventory" description="Warehouse zones, aisles, racks, and bins" icon={Package} />;
export const ShipmentsPage = () => <PlaceholderPage title="Shipments" description="Outbound shipment management" icon={Truck} />;
export const ContactsPage = () => <PlaceholderPage title="Contacts" description="Customers, vendors, and buyers" icon={Users} />;
export const CompliancePage = () => <PlaceholderPage title="Compliance" description="Certificates and destruction records" icon={Shield} />;
export const DocumentsPage = () => <PlaceholderPage title="Documents" description="Contracts, BOLs, and certificates" icon={FileText} />;
export const ReportsPage = () => <PlaceholderPage title="Reports" description="Analytics and reporting" icon={BarChart3} />;
export const SettingsPage = () => <PlaceholderPage title="Settings" description="System configuration" icon={Settings} />;
